import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { LandingHeader } from '../landing/components/header/landing-header';
import { LandingFooter } from '../landing/components/footer/landing-footer';

export function LegalLayout() {
    const { pathname } = useLocation();

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname]);

    // Reading progress bar animation
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    return (
        <div className="min-h-screen bg-background text-foreground relative selection:bg-brand-500/30 selection:text-brand-900 dark:selection:text-brand-100 flex flex-col">

            {/* Reading Progress Indicator */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-600 to-brand-400 dark:from-brand-500 dark:to-brand-300 origin-left z-50 rounded-r-full"
                style={{ scaleX }}
            />

            {/* Subtle Animated Background Elements (Parallax + Blurs) */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <motion.div
                    animate={{ y: [0, -20, 0], opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-[10%] -right-[5%] w-[600px] h-[600px] rounded-full bg-brand-500/5 blur-[120px] mix-blend-multiply dark:mix-blend-screen"
                />
                <motion.div
                    animate={{ y: [0, 30, 0], x: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-[30%] -left-[10%] w-[500px] h-[500px] rounded-full bg-brand-600/5 blur-[100px] mix-blend-multiply dark:mix-blend-screen"
                />
            </div>

            <LandingHeader />

            {/* Main Content Area */}
            <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 md:px-8 flex-1">
                <motion.div
                    initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-4xl mx-auto w-full"
                >
                    {/* Glass pane container for readability (Sophisticated reading mode) */}
                    <div className="relative p-8 sm:p-12 md:p-16 rounded-[2rem] bg-background/70 dark:bg-[#1C1714]/60 backdrop-blur-2xl border border-border/40 shadow-[0_8px_32px_rgba(0,0,0,0.04)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.16)] transition-all duration-300">

                        {/* Inner subtle glow */}
                        <div className="absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/10 pointer-events-none" />

                        <div className="prose prose-neutral dark:prose-invert max-w-none 
                            prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
                            prose-h1:text-4xl sm:prose-h1:text-5xl prose-h1:mb-8
                            prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-border/50 prose-h2:pb-4
                            prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:mt-8
                            prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:text-[1.05rem] prose-p:text-justify
                            prose-a:text-brand-600 dark:prose-a:text-brand-400 prose-a:no-underline hover:prose-a:underline prose-a:transition-all
                            prose-strong:text-foreground prose-strong:font-semibold
                            prose-ul:text-muted-foreground prose-li:my-1 prose-li:text-justify
                            prose-blockquote:border-brand-500/30 prose-blockquote:bg-brand-500/5 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-foreground/90
                            prose-th:text-foreground prose-th:font-medium prose-th:py-4 prose-th:border-b prose-th:border-border
                            prose-td:py-4 prose-td:border-b prose-td:border-border/50 prose-td:text-muted-foreground
                            [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                        ">
                            <Outlet />
                        </div>
                    </div>
                </motion.div>
            </main>

            <LandingFooter />
        </div>
    );
}
